import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  Easing,
} from 'remotion';

const PRIMARY = '#6366F1';
const SECONDARY = '#8B5CF6';
const ACCENT = '#F0ABFC';
const BG = '#0B1120';

const sleepStages = [
  { id: 'N1', name: '入睡期 N1', desc: '浅睡过渡，易被唤醒', pct: 5, color: '#818CF8' },
  { id: 'N2', name: '浅睡期 N2', desc: '睡眠纺锤波出现', pct: 45, color: '#6366F1' },
  { id: 'N3', name: '深睡期 N3', desc: '慢波睡眠，身体修复', pct: 25, color: '#4F46E5' },
  { id: 'REM', name: '快速眼动 REM', desc: '梦境活跃，记忆巩固', pct: 25, color: '#F0ABFC' },
];

const brainWaves = [
  { label: 'Beta β', hz: '14–30 Hz', color: '#A5B4FC', freq: 6, amp: 12 },
  { label: 'Alpha α', hz: '8–13 Hz', color: '#818CF8', freq: 4, amp: 18 },
  { label: 'Theta θ', hz: '4–7 Hz', color: '#C4B5FD', freq: 2.5, amp: 22 },
  { label: 'Delta δ', hz: '0.5–4 Hz', color: '#F0ABFC', freq: 1.2, amp: 28 },
];

const sleepTips = [
  { title: '规律作息', detail: '每天相近时间入睡与起床' },
  { title: '避蓝光', detail: '睡前一小时减少屏幕刺激' },
  { title: '适宜温度', detail: '卧室约 18–22℃ 更易入睡' },
  { title: '限制咖啡因', detail: '午后避免浓茶与咖啡' },
];

const summaryStats = [
  { value: '90 分钟', label: '典型睡眠周期长度' },
  { value: '7–9 小时', label: '成人建议睡眠时长' },
  { value: '20–25%', label: 'REM 占比（健康睡眠）' },
  { value: '深睡前置', label: '上半夜深睡更多' },
];

const FADE = 12;

const sectionOpacity = (
  frame: number,
  enter: number,
  exit: number,
  fade = FADE,
): number =>
  interpolate(frame, [enter - fade, enter, exit, exit + fade], [0, 1, 1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.inOut(Easing.cubic),
  });

const sectionSlide = (
  frame: number,
  enter: number,
  exit: number,
  fade = FADE,
): number =>
  interpolate(
    frame,
    [enter - fade, enter, exit, exit + fade],
    [36, 0, 0, -24],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

const buildSinePath = (
  width: number,
  yMid: number,
  amplitude: number,
  cycles: number,
  phase: number,
  steps = 100,
): string => {
  let d = '';
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const x = t * width;
    const y = yMid + Math.sin(t * Math.PI * 2 * cycles + phase) * amplitude;
    d += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`;
  }
  return d;
};

const CycleWaveChart: React.FC<{ frame: number; baseFrame: number }> = ({
  frame,
  baseFrame,
}) => {
  const rel = frame - baseFrame;
  const scroll = interpolate(rel, [0, 100], [0, 60], {
    extrapolateRight: 'clamp',
    easing: Easing.linear,
  });
  const w = 1200;
  const chartH = 260;
  const hourW = w / 8;
  const cycles = [
    { x0: 0, phases: ['N1', 'N2', 'N3', 'N2', 'REM'] },
    { x0: 90, phases: ['N2', 'N3', 'N2', 'REM'] },
    { x0: 180, phases: ['N2', 'N3', 'N2', 'REM'] },
    { x0: 270, phases: ['N2', 'N3', 'REM'] },
    { x0: 360, phases: ['N2', 'REM', 'N2'] },
  ] as const;

  const phaseColor: Record<string, string> = {
    N1: '#818CF8',
    N2: '#6366F1',
    N3: '#312E81',
    REM: '#F0ABFC',
  };

  return (
    <svg
      width={w}
      height={chartH + 50}
      viewBox={`${-scroll} 0 ${w + scroll} ${chartH + 50}`}
      style={{ overflow: 'visible' }}
    >
      <defs>
        <linearGradient id="sleepChartBg" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={PRIMARY} stopOpacity="0.15" />
          <stop offset="100%" stopColor={SECONDARY} stopOpacity="0.08" />
        </linearGradient>
      </defs>
      <rect
        x={-scroll} y={0}
        width={w + scroll + 40} height={chartH}
        rx={12} fill="url(#sleepChartBg)"
      />
      {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((hourMark) => (
        <g key={hourMark}>
          <line
            x1={hourMark * hourW} y1={0}
            x2={hourMark * hourW} y2={chartH}
            stroke="rgba(255,255,255,0.06)" strokeWidth={1}
          />
          <text
            x={hourMark * hourW + 4} y={chartH + 24}
            fill="rgba(255,255,255,0.45)" fontSize={26}
            fontFamily="system-ui, sans-serif"
          >
            {hourMark}h
          </text>
        </g>
      ))}
      <text
        x={8} y={22}
        fill="rgba(255,255,255,0.7)" fontSize={26} fontWeight={600}
        fontFamily="system-ui, sans-serif"
      >
        整夜睡眠周期示意（约 90 分钟重复）
      </text>
      {cycles.flatMap((c, ci) => {
        const pxPerMin = w / (8 * 60);
        let acc = c.x0 * pxPerMin;
        return c.phases.map((p, pi) => {
          const segW = p === 'REM' ? 22 * pxPerMin : 17 * pxPerMin;
          const x = acc;
          acc += segW;
          return (
            <rect
              key={`${ci}-${pi}`}
              x={x}
              y={36 + (p === 'REM' ? 70 : 110)}
              width={segW}
              height={p === 'REM' ? 90 : 55}
              rx={6}
              fill={phaseColor[p]}
              opacity={0.85}
            />
          );
        });
      })}
      <path
        d={buildSinePath(w, 85, 16, 5.5, rel * 0.08, 120)}
        fill="none" stroke={ACCENT} strokeWidth={2.5} opacity={0.6}
      />
    </svg>
  );
};

const CircadianClock: React.FC<{ frame: number; size?: number }> = ({
  frame,
  size = 160,
}) => {
  const handAngle = interpolate(frame, [0, 540], [-90, 270], {
    extrapolateRight: 'extend',
  });
  const r = size / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <defs>
        <linearGradient id="clockFace" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={SECONDARY} stopOpacity="0.4" />
          <stop offset="100%" stopColor={PRIMARY} stopOpacity="0.2" />
        </linearGradient>
      </defs>
      <circle cx={r} cy={r} r={r - 4} fill="url(#clockFace)" stroke={PRIMARY} strokeWidth={2} />
      {Array.from({ length: 12 }).map((_, i) => {
        const ang = (i / 12) * Math.PI * 2 - Math.PI / 2;
        const x1 = r + Math.cos(ang) * (r - 14);
        const y1 = r + Math.sin(ang) * (r - 14);
        const x2 = r + Math.cos(ang) * (r - 6);
        const y2 = r + Math.sin(ang) * (r - 6);
        return (
          <line
            key={i}
            x1={x1} y1={y1} x2={x2} y2={y2}
            stroke="rgba(255,255,255,0.35)" strokeWidth={2}
          />
        );
      })}
      <text
        x={r} y={24} textAnchor="middle"
        fill={ACCENT} fontSize={16} fontFamily="system-ui, sans-serif"
      >
        24h
      </text>
      <line
        x1={r} y1={r}
        x2={r + Math.cos((handAngle * Math.PI) / 180) * (r - 28)}
        y2={r + Math.sin((handAngle * Math.PI) / 180) * (r - 28)}
        stroke={ACCENT} strokeWidth={3} strokeLinecap="round"
      />
      <circle cx={r} cy={r} r={6} fill={PRIMARY} />
    </svg>
  );
};

export const SleepScienceAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const opTitle = sectionOpacity(frame, 10, 78, FADE);
  const opStages = sectionOpacity(frame, 88, 178, FADE);
  const opCycle = sectionOpacity(frame, 188, 290, FADE);
  const opBrain = sectionOpacity(frame, 300, 390, FADE);
  const opTips = sectionOpacity(frame, 400, 475, FADE);
  const opSummary = sectionOpacity(frame, 485, 530, FADE);

  const slideTitle = sectionSlide(frame, 10, 78, FADE);
  const slideStages = sectionSlide(frame, 88, 178, FADE);
  const slideCycle = sectionSlide(frame, 188, 290, FADE);
  const slideBrain = sectionSlide(frame, 300, 390, FADE);
  const slideTips = sectionSlide(frame, 400, 475, FADE);
  const slideSummary = sectionSlide(frame, 485, 530, FADE);

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(160deg, ${BG} 0%, #08090c 45%, #0F1419 100%)`,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse 80% 50% at 20% 20%, rgba(99,102,241,0.18), transparent 55%)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse 60% 40% at 85% 75%, rgba(240,171,252,0.12), transparent 50%)',
          pointerEvents: 'none',
        }}
      />

      {/* Section 1: Title */}
      {opTitle > 0 && (
        <AbsoluteFill
          style={{
            opacity: opTitle,
            transform: `translateY(${slideTitle}px)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 48,
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <h1
              style={{
                margin: 0,
                fontSize: 76,
                fontWeight: 800,
                letterSpacing: -1,
                background: `linear-gradient(120deg, ${PRIMARY}, ${ACCENT})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              睡眠科学
            </h1>
            <p
              style={{
                margin: '14px 0 0',
                fontSize: 30,
                color: 'rgba(255,255,255,0.55)',
                fontWeight: 500,
              }}
            >
              提升睡眠质量的秘密 · Sleep Stages &amp; Circadian Rhythm
            </p>
          </div>
          <CircadianClock frame={frame} size={240} />
        </AbsoluteFill>
      )}

      {/* Section 2: Sleep Stages */}
      {opStages > 0 && (
        <AbsoluteFill
          style={{
            opacity: opStages,
            transform: `translateY(${slideStages}px)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 72px',
          }}
        >
          <h2
            style={{
              margin: '0 0 80px',
              fontSize: 38,
              color: '#fff',
              fontWeight: 700,
              textAlign: 'center',
            }}
          >
            睡眠分期概览
          </h2>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              width: '100%',
              maxWidth: 1100,
            }}
          >
            {sleepStages.map((s, i) => {
              const d = spring({
                frame: frame - 92 - i * 12,
                fps,
                config: { damping: 12, stiffness: 180 },
              });
              return (
                <div
                  key={s.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 20,
                    padding: '16px 22px',
                    borderRadius: 14,
                    background: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${s.color}44`,
                    opacity: d,
                    transform: `translateX(${interpolate(d, [0, 1], [-30, 0])}px)`,
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(100, s.pct)}%`,
                      maxWidth: 420,
                      height: 36,
                      borderRadius: 8,
                      background: `linear-gradient(90deg, ${s.color}, ${SECONDARY})`,
                      boxShadow: `0 0 24px ${s.color}33`,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#fff' }}>{s.name}</div>
                    <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
                      {s.desc}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 30,
                      fontWeight: 800,
                      color: s.color,
                      width: 80,
                      textAlign: 'right',
                    }}
                  >
                    {s.pct}%
                  </span>
                </div>
              );
            })}
          </div>
        </AbsoluteFill>
      )}

      {/* Section 3: Sleep Cycle Wave Chart */}
      {opCycle > 0 && (
        <AbsoluteFill
          style={{
            opacity: opCycle,
            transform: `translateY(${slideCycle}px)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CycleWaveChart frame={frame} baseFrame={192} />
        </AbsoluteFill>
      )}

      {/* Section 4: Brain Waves */}
      {opBrain > 0 && (
        <AbsoluteFill
          style={{
            opacity: opBrain,
            transform: `translateY(${slideBrain}px)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 72px',
          }}
        >
          <h2
            style={{
              margin: '0 0 80px',
              fontSize: 38,
              color: '#fff',
              fontWeight: 700,
              textAlign: 'center',
            }}
          >
            脑电波型对比
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 20,
              width: '100%',
              maxWidth: 1100,
            }}
          >
            {brainWaves.map((bw, i) => {
              const phase = frame * 0.12 + i * 0.8;
              const path = buildSinePath(700, 60, bw.amp, bw.freq, phase, 140);
              const p = spring({
                frame: frame - 304 - i * 10,
                fps,
                config: { damping: 11, stiffness: 160 },
              });
              return (
                <div
                  key={bw.label}
                  style={{
                    padding: 18,
                    borderRadius: 16,
                    background: 'rgba(255,255,255,0.03)',
                    border: `1px solid ${bw.color}33`,
                    opacity: p,
                    transform: `scale(${interpolate(p, [0, 1], [0.94, 1])})`,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 10,
                    }}
                  >
                    <span style={{ fontSize: 26, fontWeight: 700, color: bw.color }}>
                      {bw.label}
                    </span>
                    <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.45)' }}>
                      {bw.hz}
                    </span>
                  </div>
                  <svg
                    width="100%" height={90}
                    viewBox="0 0 700 90" preserveAspectRatio="none"
                  >
                    <path
                      d={path}
                      fill="none" stroke={bw.color} strokeWidth={2.5}
                      vectorEffect="non-scaling-stroke"
                    />
                  </svg>
                </div>
              );
            })}
          </div>
        </AbsoluteFill>
      )}

      {/* Section 5: Sleep Tips */}
      {opTips > 0 && (
        <AbsoluteFill
          style={{
            opacity: opTips,
            transform: `translateY(${slideTips}px)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 72px',
          }}
        >
          <h2
            style={{
              margin: '0 0 80px',
              fontSize: 38,
              color: '#fff',
              fontWeight: 700,
              textAlign: 'center',
            }}
          >
            改善睡眠小贴士
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 22,
              width: '100%',
              maxWidth: 1500,
            }}
          >
            {sleepTips.map((tip, i) => {
              const s = spring({
                frame: frame - 404 - i * 12,
                fps,
                config: { damping: 13, stiffness: 200 },
              });
              return (
                <div
                  key={tip.title}
                  style={{
                    padding: '22px 20px',
                    borderRadius: 16,
                    background: `linear-gradient(145deg, rgba(99,102,241,0.2), rgba(139,92,246,0.08))`,
                    border: `1px solid ${PRIMARY}44`,
                    opacity: s,
                    transform: `translateY(${interpolate(s, [0, 1], [24, 0])}px)`,
                    boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background: ACCENT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#1e1b4b',
                        fontWeight: 800,
                        fontSize: 18,
                        flexShrink: 0,
                      }}
                    >
                      {i + 1}
                    </div>
                    <div
                      style={{
                        fontSize: 30,
                        fontWeight: 700,
                        color: '#fff',
                      }}
                    >
                      {tip.title}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 26,
                      color: 'rgba(255,255,255,0.55)',
                      lineHeight: 1.5,
                    }}
                  >
                    {tip.detail}
                  </div>
                </div>
              );
            })}
          </div>
        </AbsoluteFill>
      )}

      {/* Section 6: Summary Stats */}
      {opSummary > 0 && (
        <AbsoluteFill
          style={{
            opacity: opSummary,
            transform: `translateY(${slideSummary}px)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 72px',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: 24,
              width: '100%',
              maxWidth: 1300,
            }}
          >
            {summaryStats.map((st, i) => {
              const s = spring({
                frame: frame - 488 - i * 8,
                fps,
                config: { damping: 12, stiffness: 220 },
              });
              return (
                <div
                  key={st.label}
                  style={{
                    flex: 1,
                    padding: '28px 20px',
                    borderRadius: 14,
                    textAlign: 'center',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(240,171,252,0.25)',
                    opacity: s,
                  }}
                >
                  <div
                    style={{
                      fontSize: 36,
                      fontWeight: 800,
                      background: `linear-gradient(90deg, ${PRIMARY}, ${ACCENT})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {st.value}
                  </div>
                  <div
                    style={{
                      fontSize: 26,
                      color: 'rgba(255,255,255,0.5)',
                      marginTop: 10,
                    }}
                  >
                    {st.label}
                  </div>
                </div>
              );
            })}
          </div>
        </AbsoluteFill>
      )}

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 4,
          background: 'rgba(255,255,255,0.06)',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${(frame / 540) * 100}%`,
            background: `linear-gradient(90deg, ${PRIMARY}, ${ACCENT})`,
            borderRadius: 2,
          }}
        />
      </div>
    </AbsoluteFill>
  );
};
